'use client';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Dialog as DialogPrimitive } from 'radix-ui';
import { tv } from 'tailwind-variants';

import { faXmark } from '@icons/classic/regular';
import { heading } from '../typography/Heading';

// DIALOG
function Dialog({ ...props }: React.ComponentProps<typeof DialogPrimitive.Root>): React.ReactElement {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />;
}

// DIALOG TRIGGER
function DialogTrigger({ ...props }: React.ComponentProps<typeof DialogPrimitive.Trigger>): React.ReactElement {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

// DIALOG PORTAL
function DialogPortal({ ...props }: React.ComponentProps<typeof DialogPrimitive.Portal>): React.ReactElement {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

// DIALOG CLOSE
function DialogClose({ ...props }: React.ComponentProps<typeof DialogPrimitive.Close>): React.ReactElement {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

// DIALOG OVERLAY
const dialogOverlay = tv({
  base: `
    data-[state=open]:animate-in
    data-[state=closed]:animate-out
    data-[state=closed]:fade-out-0
    data-[state=open]:fade-in-0
    fixed
    inset-0
    z-50
  	grid
    place-items-center
    overflow-y-auto
    py-8
  `,
  variants: {
    showBackground: {
      true: 'bg-black/50',
      false: 'bg-transparent',
    },
  },
  defaultVariants: {
    showBackground: true,
  },
});

interface DialogOverlayProps extends React.ComponentProps<typeof DialogPrimitive.Overlay> {
  showBackground?: boolean;
}

function DialogOverlay({ className, showBackground = true, ...props }: DialogOverlayProps): React.ReactElement {
  return (
    <DialogPrimitive.Overlay
      className={dialogOverlay({ className, showBackground })}
      data-slot="dialog-overlay"
      {...props}
    />
  );
}

// DIALOG CONTENT
const dialogContent = tv({
  slots: {
    base: `
      bg-background
      data-[state=open]:animate-in
      data-[state=closed]:animate-out
      data-[state=closed]:fade-out-0
      data-[state=open]:fade-in-0
      data-[state=closed]:zoom-out-95
      data-[state=open]:zoom-in-95
      z-50
      grid
      w-full
      max-w-[calc(100%-2rem)]
      gap-4
      rounded-lg
      shadow-lg
      duration-200
      relative
    `,
    close: `
      ring-offset-background
      focus:ring-ring
      data-[state=open]:bg-accent
      data-[state=open]:text-muted-foreground
      absolute
      top-4
      right-4
      rounded-xs
      opacity-70
      transition-opacity
      hover:opacity-100
      focus:ring-2
      focus:ring-offset-2
      focus:outline-hidden
      cursor-pointer
      disabled:pointer-events-none
      [&_svg]:pointer-events-none
      [&_svg]:shrink-0
      [&_svg:not([class*='size-'])]:size-4
      `,
  },
  variants: {
    /** `none` is for content that paints its own chrome, e.g. a search panel filling the dialog. */
    padding: {
      default: {
        base: 'p-4 sm:p-6',
      },
      none: {
        base: 'p-0',
      },
    },
    size: {
      sm: {
        base: 'max-w-lg',
      },
      md: {
        base: 'max-w-xl',
      },
      lg: {
        base: 'max-w-2xl',
      },
      xl: {
        base: 'max-w-4xl',
      },
      '2xl': {
        base: 'max-w-6xl',
      },
    },
  },
  defaultVariants: {
    padding: 'default',
  },
});

interface DialogContentProps extends React.ComponentProps<typeof DialogPrimitive.Content> {
  closeable?: boolean;
  padding?: 'default' | 'none';
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  showOverlayBackground?: boolean;
  onCloseClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  /** Called on any close attempt (X click, Escape, outside click). Call event.preventDefault() to prevent closing. */
  onCloseAttempt?: (event: { preventDefault: () => void }) => void;
}
function DialogContent({
  className,
  children,
  closeable = true,
  padding = 'default',
  size = 'md',
  showOverlayBackground = true,
  onCloseClick,
  onCloseAttempt,
  onEscapeKeyDown,
  onInteractOutside,
  ...props
}: DialogContentProps): React.ReactElement {
  const { base, close } = dialogContent({ padding, size });

  return (
    <DialogPortal data-slot="dialog-portal">
      <DialogOverlay showBackground={showOverlayBackground}>
        <DialogPrimitive.Content
          className={base({ className })}
          data-slot="dialog-content"
          onEscapeKeyDown={(e) => {
            onEscapeKeyDown?.(e);
            if (!e.defaultPrevented) {
              onCloseAttempt?.(e);
            }
          }}
          onInteractOutside={(e) => {
            onInteractOutside?.(e);
            if (!e.defaultPrevented) {
              onCloseAttempt?.(e);
            }
          }}
          {...props}
        >
          {children}
          {closeable && (
            <DialogPrimitive.Close
              className={close()}
              onClick={(e) => {
                onCloseClick?.(e);
                if (!e.defaultPrevented) {
                  onCloseAttempt?.(e);
                }
              }}
            >
              <FontAwesomeIcon icon={faXmark} />
              <span className="sr-only">Close</span>
            </DialogPrimitive.Close>
          )}
        </DialogPrimitive.Content>
      </DialogOverlay>
    </DialogPortal>
  );
}

// DIALOG HEADER
const dialogHeader = tv({
  base: `
    flex flex-col gap-2 text-left
  `,
});
function DialogHeader({ className, ...props }: React.ComponentProps<'div'>): React.ReactElement {
  return <div className={dialogHeader({ className })} data-slot="dialog-header" {...props} />;
}

// DIALOG FOOTER
const dialogFooter = tv({
  base: `
    flex flex-col-reverse gap-2 sm:flex-row sm:justify-end
    w-full
  `,
});
function DialogFooter({ className, ...props }: React.ComponentProps<'div'>): React.ReactElement {
  return <div className={dialogFooter({ className })} data-slot="dialog-footer" {...props} />;
}

// DIALOG TITLE
const dialogTitle = tv({
  extend: heading,
  defaultVariants: {
    size: 'md',
    weight: 'medium',
  },
});
function DialogTitle({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Title>): React.ReactElement {
  return (
    <DialogPrimitive.Title className={dialogTitle({ className })} data-slot="dialog-title" tabIndex={0} {...props} />
  );
}

// DIALOG DESCRIPTION
const dialogDescription = tv({
  base: `
    text-muted-foreground text-sm
  `,
});
function DialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>): React.ReactElement {
  return (
    <DialogPrimitive.Description
      className={dialogDescription({ className })}
      data-slot="dialog-description"
      {...props}
    />
  );
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
